import { cellToBoundary } from "h3-js";
import type { FeatureCollection } from "geojson";
import { stateAt } from "./schedule.js";
import { venueCategories, type CellSummary, type Venue, type VenueCategory } from "./types.js";

function emptyCategoryCounts(): Record<VenueCategory, number> {
  return { food: 0, groceries: 0, retail: 0, services: 0, entertainment: 0 };
}

export function filterVenuesByCategory(
  venues: readonly Venue[],
  categories: ReadonlySet<VenueCategory>,
): Venue[] {
  return venues.filter((venue) => categories.has(venue.category));
}

export function aggregateCells(venues: readonly Venue[], slot: number): CellSummary[] {
  const cells = new Map<string, CellSummary>();
  venues.forEach((venue) => {
    const state = stateAt(venue.schedule, slot);
    const cell = cells.get(venue.h3) ?? {
      h3: venue.h3,
      open: 0,
      possible: 0,
      total: 0,
      known: 0,
      missingHours: 0,
      categories: emptyCategoryCounts(),
      openVenues: [],
    };
    cell.total += 1;
    cell.categories[venue.category] += 1;
    if (!venue.originalHours?.trim() && !venue.kitchenHours?.trim()) {
      cell.missingHours += 1;
    }
    if (state === "open") {
      cell.open += 1;
      cell.known += 1;
      if (cell.openVenues.length < 8) cell.openVenues.push(venue);
    } else if (state === "closed") {
      cell.known += 1;
    } else {
      cell.possible += 1;
    }
    cells.set(venue.h3, cell);
  });
  return [...cells.values()];
}

export function cellsToGeoJson(cells: readonly CellSummary[]): FeatureCollection {
  return {
    type: "FeatureCollection",
    features: cells.map((cell) => {
      const boundary = cellToBoundary(cell.h3, true);
      const ring = [...boundary, boundary[0]!];
      return {
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [ring] },
        properties: {
          h3: cell.h3,
          open: cell.open,
          possible: cell.possible,
          total: cell.total,
          known: cell.known,
          missingHours: cell.missingHours,
          hoursCoveragePercent: cell.total
            ? Math.round(((cell.total - cell.missingHours) / cell.total) * 100)
            : 0,
          knownPercent: cell.total ? Math.round((cell.known / cell.total) * 100) : 0,
          intensity: Math.min(1, Math.log2(cell.open + 1) / 4),
        },
      };
    }),
  } as FeatureCollection;
}

export function coverageCells(cells: readonly CellSummary[]): CellSummary[] {
  return cells.filter((cell) => cell.open > 0);
}

export function venuesToGeoJson(venues: readonly Venue[], slot: number): FeatureCollection {
  return {
    type: "FeatureCollection",
    features: venues.map((venue) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: venue.coordinates },
      properties: {
        id: venue.id,
        h3: venue.h3,
        category: venue.category,
        state: stateAt(venue.schedule, slot),
      },
    })),
  } as FeatureCollection;
}

export function openVenuesToGeoJson(venues: readonly Venue[], slot: number): FeatureCollection {
  return venuesToGeoJson(
    venues.filter((venue) => stateAt(venue.schedule, slot) === "open"),
    slot,
  );
}

export { venueCategories };
